import { ChevronRight, LogOut, Wallet } from "lucide-react";
import { useGetIdentity, useGetOne, useLogout, useTranslate } from "ra-core";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";
import type { Sale } from "@/components/atomic-crm/types";

import { MiHorario } from "./MiHorario";
import { AvisosMovil } from "../avisos/AvisosMovil";
import { useCon36 } from "../datos/useFiltroEtapas";
import { useMiRol } from "../datos/useMiRol";
import { MisComisionesPage } from "../equipo/MisComisionesPage";
import { EditorFoto } from "../usuarios/FotoUsuario";

/**
 * Perfil del usuario (FASE 1: solo lectura). Editar nombre/email y cambiar la contraseña
 * desde aquí necesita las edge functions `users` y `update_password`, que se despliegan
 * en FASE 4; mientras tanto la contraseña se cambia con «¿Has olvidado tu contraseña?».
 */
export const PaginaPerfil = () => {
  const translate = useTranslate();
  const logout = useLogout();
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const { data: usuario } = useGetOne<Sale & { foto?: string | null }>(
    "sales",
    { id: identity?.id as number },
    { enabled: !!identity?.id },
  );
  const { vende, esConsultor, esAdmin } = useMiRol();
  const con36 = useCon36();

  const fila = (etiqueta: string, valor?: string) => (
    <div className="flex justify-between gap-4 py-3 border-b last:border-b-0">
      <span className="text-muted-foreground">{etiqueta}</span>
      <span className="font-medium text-right">{valor ?? "—"}</span>
    </div>
  );

  const contenido = (
    <section className="space-y-6 py-4 max-w-xl">
      {!isMobile ? (
        <h1 className="titulo-pagina">{translate("nevora.perfil.titulo")}</h1>
      ) : null}
      {/* 30: la foto, solo si la base ya la tiene (la fila trae `foto`). */}
      {usuario && "foto" in usuario ? (
        <div className="rounded-lg border bg-card px-5 py-4">
          <EditorFoto usuario={usuario} />
        </div>
      ) : null}
      <div className="rounded-lg border bg-card px-5">
        {fila(
          translate("nevora.perfil.nombre"),
          usuario
            ? `${usuario.first_name} ${usuario.last_name}`
            : identity?.fullName,
        )}
        {fila(translate("nevora.perfil.email"), usuario?.email)}
        {fila(
          translate("nevora.perfil.rol"),
          usuario
            ? translate(
                usuario.administrator
                  ? "nevora.perfil.admin"
                  : "nevora.perfil.comercial",
              )
            : undefined,
        )}
        <div className="flex justify-between items-center gap-4 py-3">
          <span className="text-muted-foreground">
            {translate("nevora.perfil.tema")}
          </span>
          <ThemeModeToggle />
        </div>
      </div>
      {(vende || esConsultor) && con36 ? (
        // 36: en el móvil no hay barra lateral; «Mis comisiones» se abre desde aquí.
        <Link
          to={MisComisionesPage.path}
          className="flex items-center gap-3 rounded-lg border bg-card px-5 py-4 font-medium"
          data-testid="enlace-mis-comisiones"
        >
          <Wallet className="size-5 text-muted-foreground" />
          <span className="flex-1">
            {translate("nevora.nav.mis_comisiones")}
          </span>
          <ChevronRight className="size-4 text-muted-foreground" />
        </Link>
      ) : null}
      <AvisosMovil />
      {/* 41: quien hace consultorías pone aquí su horario y su calendario de Google. */}
      {esAdmin || esConsultor ? <MiHorario /> : null}
      <p className="text-sm text-muted-foreground">
        {translate("nevora.perfil.cambiar_contrasena_ayuda")}
      </p>
      <Button
        variant="outline"
        className="w-full sm:w-auto"
        onClick={() => logout()}
      >
        <LogOut className="size-4 mr-2" />
        {translate("nevora.perfil.cerrar_sesion")}
      </Button>
    </section>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="titulo-seccion">
            {translate("nevora.perfil.titulo")}
          </h1>
        </MobileHeader>
        <MobileContent>{contenido}</MobileContent>
      </>
    );
  }
  return contenido;
};

PaginaPerfil.path = "/perfil";
